import { ConvexError } from 'convex/values';
import type { ErrorCode } from '@circles/convex';

export type ErrorInfo = { code: ErrorCode | null; message: string };

/**
 * Normalises anything thrown by a Convex call into `{ code, message }`. Functions throw
 * `ConvexError({ code, message })` (architecture §2); anything else is an unexpected
 * server or network error whose message is stripped of Convex's request prefixes.
 */
export function errorInfo(err: unknown): ErrorInfo {
  if (err instanceof ConvexError) {
    const data = err.data as unknown;
    if (data && typeof data === 'object' && 'message' in data) {
      const d = data as { code?: ErrorCode; message?: unknown };
      return { code: d.code ?? null, message: String(d.message) };
    }
    return { code: null, message: typeof data === 'string' ? data : 'Something went wrong' };
  }
  if (err instanceof Error) {
    const cleaned = err.message
      .replace(/\[CONVEX [^\]]*\]\s*/g, '')
      .replace(/\[Request ID: [^\]]*\]\s*/g, '')
      .replace(/^Server Error\s*/, '')
      .replace(/^Uncaught Error:\s*/, '')
      .split('\n')[0]!
      .trim();
    return { code: null, message: cleaned || 'Something went wrong' };
  }
  return { code: null, message: String(err) };
}

export function errorMessage(err: unknown): string {
  return errorInfo(err).message;
}
